import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import ContentBlock from '@/components/molecules/ContentBlock';
import LiceStatus from '@/components/molecules/LiceStatus';
import type { Image } from '@/components/organisms/ImageViewer';
import LatestImageCard from '@/components/organisms/LatestImageCard';
import type { RetryProps } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import type { DomainDailyLiceCount, DomainLast3DaysLiceCount } from '@/domain/lice';
import OverviewChartCard from '@/routes/Client/Cage/overviewCharts/OverviewChartCard';
import { parseLocale } from '@/utils/localeFormatters';
import { createCageUrl } from '@/utils/urlGenerator';

import { buildFishCountChartSpec, buildLiceCountChartSpec } from '../liceCharts/liceChartSpecs';

import styles from './CageLiceDashboard.module.scss';

interface CageLiceDashboardPageContentProps {
    clientId: number;
    locationId: number;
    cageId: number;
    last200?: DomainLast3DaysLiceCount | null;
    liceCount: DomainDailyLiceCount[];
    licePageUrl?: string;
    images?: Image[];
    liceImagesLoading: boolean;
    /** Try again for the detection image, set only once its request failed. */
    liceImagesError?: RetryProps | null;
    /** Try again for the three-day averages, set only once their request failed. */
    last200Error?: RetryProps | null;
    hasLiceLicense: boolean;
    isChileanLice: boolean;
    isLoading?: boolean;
    error?: unknown;
    /** Try again in the charts' error state. */
    retry?: RetryProps;
}

export default function CageLiceDashboardPageContent({
    clientId,
    locationId,
    cageId,
    last200,
    liceCount,
    licePageUrl,
    images,
    liceImagesLoading,
    liceImagesError,
    last200Error,
    hasLiceLicense,
    isChileanLice,
    isLoading,
    error,
    retry,
}: CageLiceDashboardPageContentProps) {
    const { t } = useTranslation();
    const locale = parseLocale(i18n.language);

    /** Chile does not track Caligus elongatus, and names the adult female count differently. */
    const labels = {
        female: t(
            isChileanLice ? 'cageLice.common.chileanFemaleLice' : 'cageLice.common.matureFemaleLice'
        ),
        movingStage: t('cageLice.common.mobileLice'),
        caligus: t('cageLice.common.caligus'),
        liceAxis: t('cageLice.common.liceCount'),
        temperatureAxis: t('common.labels.temperature'),
        temperature: t('common.labels.temperature'),
    };

    const shared = { isLoading, error, ...retry, showDateRangePicker: false };

    return (
        <div className={styles.pageBoxLayout}>
            <ContentBlock
                title={t('cageLice.dashboard.averageNumberOfLicePer')}
                subtitle={t('cageLice.dashboard.theNumbersShowsTheAverages')}
                fill
                className={styles.topBlock}>
                {last200Error ? (
                    <SomethingHappened {...last200Error} />
                ) : (
                    <LiceStatus
                        liceInMin200Fish={last200}
                        licePageUrl={licePageUrl}
                        hasLiceLicense={hasLiceLicense}
                        isChileanLice={isChileanLice}
                        isLoading={isLoading}
                    />
                )}
            </ContentBlock>

            <LatestImageCard
                title={t('common.labels.detections')}
                imageAlt={t('cageLice.dashboard.liceImage')}
                images={images}
                isLoading={liceImagesLoading}
                loadError={liceImagesError}
                url={`${createCageUrl(clientId, locationId, cageId)}/lice/detections`}
                className={styles.topBlock}
            />

            <OverviewChartCard
                title={t('cageLice.common.liceCountAndTemperature')}
                chartName="cage-lice-count-temperature"
                csvExport={false}
                spec={buildLiceCountChartSpec({
                    days: liceCount,
                    metrics: isChileanLice
                        ? ['female', 'movingStage']
                        : ['female', 'movingStage', 'caligus'],
                    labels,
                    locale,
                })}
                {...shared}
            />

            <OverviewChartCard
                title={t('cageLice.common.fishCount')}
                chartName="cage-lice-fish-count"
                csvExport={false}
                spec={buildFishCountChartSpec({
                    days: liceCount,
                    label: t('cageLice.common.fishCount'),
                    axisLabel: t('cageLice.common.fishCount'),
                    locale,
                })}
                {...shared}
            />
        </div>
    );
}
